/**
 * PatientRegistration.tsx - Registro manual de pacientes por terapeuta
 * 
 * 🧑‍⚕️ Permite al terapeuta:
 * - Registrar nuevos pacientes manualmente
 * - Agendar citas inmediatamente después del registro
 * - Validar datos básicos del paciente
 * 
 * @version 1.0.0
 * @author Conectemos Chile
 */

import React, { useState } from 'react';
import { supabase } from '../../lib/supabase/client';
import { validateRut as validateRutCheckDigit, formatRut as formatRutDisplay, cleanRut } from '../../lib/utils/rutValidator';
import './PatientRegistration.css';

interface PatientData {
  rut: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  fecha_nacimiento: string;
  genero: 'masculino' | 'femenino' | 'no-binario' | 'prefiero-no-decir' | '';
  direccion: string;
  condiciones_medicas: string;
  medicamentos_actuales: string;
  contacto_emergencia_nombre: string;
  contacto_emergencia_telefono: string;
}

interface PatientRegistrationProps {
  onPatientRegistered: (patient: any) => void;
  onCancel: () => void;
  therapistId: string;
  compact?: boolean;
}

const PatientRegistration: React.FC<PatientRegistrationProps> = ({
  onPatientRegistered,
  onCancel,
  therapistId,
  compact = false
}) => {
  const [patientData, setPatientData] = useState<PatientData>({
    rut: '',
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    fecha_nacimiento: '',
    genero: '',
    direccion: '',
    condiciones_medicas: '',
    medicamentos_actuales: '',
    contacto_emergencia_nombre: '',
    contacto_emergencia_telefono: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<PatientData>>({});

  const validateRut = (rut: string): boolean => validateRutCheckDigit(rut);

  // Formato canónico para BD: "XXXXXXXX-D" (sin puntos, dígito verificador en mayúscula)
  const normalizeRutForDB = (rut: string): string => {
    const clean = cleanRut(rut); // quita todo excepto [0-9kK], mayúsculas
    if (clean.length < 2) return clean;
    return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
  };

  const handleInputChange = (field: keyof PatientData, value: string) => {
    if (field === 'rut') {
      const cleaned = value.replace(/[^0-9kK]/g, '');
      value = cleaned.length >= 8 ? formatRutDisplay(cleaned) : cleaned.toUpperCase();
    }
    
    setPatientData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error cuando el usuario empieza a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<PatientData> = {};
    
    // Campos obligatorios
    if (!patientData.rut.trim()) newErrors.rut = 'RUT es obligatorio';
    else if (!validateRut(patientData.rut)) newErrors.rut = 'RUT inválido';
    
    if (!patientData.nombre.trim()) newErrors.nombre = 'Nombre es obligatorio';
    if (!patientData.apellido.trim()) newErrors.apellido = 'Apellido es obligatorio';
    if (!patientData.email.trim()) newErrors.email = 'Email es obligatorio';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(patientData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!patientData.telefono.trim()) newErrors.telefono = 'Teléfono es obligatorio';
    if (!compact && !patientData.fecha_nacimiento) newErrors.fecha_nacimiento = 'Fecha de nacimiento es obligatoria';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const rutCanonical = normalizeRutForDB(patientData.rut);
      const emailNormalized = patientData.email.toLowerCase().trim();

      // Verificar duplicado por RUT (formato canónico para coincidir con la BD)
      const { data: byRut } = await supabase
        .from('pacientes')
        .select('id')
        .eq('rut', rutCanonical)
        .limit(1);

      if (byRut && byRut.length > 0) {
        setErrors({ rut: 'Ya existe un paciente con este RUT' });
        setIsSubmitting(false);
        return;
      }

      // Verificar duplicado por email
      const { data: byEmail } = await supabase
        .from('pacientes')
        .select('id')
        .eq('email', emailNormalized)
        .limit(1);

      if (byEmail && byEmail.length > 0) {
        setErrors({ email: 'Ya existe un paciente con este email' });
        setIsSubmitting(false);
        return;
      }

      // 'genero' → columna pacientes.genero (CHECK valid_genero:
      // femenino / masculino / no-binario / prefiero-no-decir). Vacío → null.
      const { genero, ...dbFields } = patientData;
      const { data: newPatient, error } = await supabase
        .from('pacientes')
        .insert([{
          ...(compact ? {
            nombre: patientData.nombre.trim(),
            apellido: patientData.apellido.trim(),
            telefono: patientData.telefono.trim(),
          } : dbFields),
          rut: rutCanonical,
          email: emailNormalized,
          genero: genero || null,
          terapeuta_id: therapistId
          // created_at: lo pone el DEFAULT de la BD (timezone('utc', now())) —
          // el cliente no fija timestamps de creación (SEC-051 fast-follow).
        }])
        // SEC-064: `authenticated` pasó de SELECT de tabla completa a
        // allowlist columnar en `pacientes` (ver sql/migrations/sec064_...).
        // `.select()` sin columnas pedía select=* → 42501, abortaba el
        // INSERT entero. Lista = exactamente el GRANT SELECT de esa
        // migración (42 columnas, todas menos invitacion_token_hash) —
        // preserva el mismo dato que devolvía antes, columna por columna.
        .select(
          'id, created_at, updated_at, nombre, apellido, email, telefono, rut, ' +
          'fecha_nacimiento, direccion, comuna, ciudad, prevision, nombre_isapre, ' +
          'numero_beneficiario, contacto_emergencia_nombre, contacto_emergencia_telefono, ' +
          'contacto_emergencia_relacion, alergias, medicamentos_actuales, ' +
          'condiciones_medicas, activo, primera_consulta, terapeuta_id, ' +
          'inversionista_id, consulta_inicio_split, porcentaje_inversionista, ' +
          'tarifa_diferenciada, tarifa_diferenciada_nota, nombre_pareja, genero, ' +
          'estado_relacion, bloqueado_clinica, bloqueo_motivo, bloqueo_por, bloqueo_at, ' +
          'auth_user_id, estado_credencial, invitacion_expira, invitacion_enviada_at, ' +
          'invitacion_aprobada_por, clave_actualizada_at'
        )
        .single();

      if (error) {
        console.error('❌ Error creando paciente:', error);
        throw new Error('Error al registrar paciente');
      }

      onPatientRegistered(newPatient);
      
    } catch (error: any) {
      console.error('❌ Error en registro:', error);
      setErrors({ rut: error.message || 'Error al registrar paciente' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`patient-registration-modal${compact ? ' patient-registration-modal--compact' : ''}`}>
      <div className="modal-backdrop" onClick={onCancel} />
      <div className="registration-form-container">
        <div className="form-header">
          <h2>Registrar Nuevo Paciente</h2>
          <button className="close-btn" onClick={onCancel}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="patient-form">
          <div className="form-grid">
            {/* Datos personales */}
            <div className="form-section">
              {!compact && <h3>Datos Personales</h3>}
              
              <div className="form-group">
                <label htmlFor="rut">RUT *</label>
                <input
                  type="text"
                  id="rut"
                  value={patientData.rut}
                  onChange={(e) => handleInputChange('rut', e.target.value)}
                  placeholder="12.345.678-9"
                  className={errors.rut ? 'error' : ''}
                  maxLength={12}
                />
                {errors.rut && <span className="error-message">{errors.rut}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nombre">Nombre *</label>
                  <input
                    type="text"
                    id="nombre"
                    value={patientData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    placeholder="Nombre del paciente"
                    className={errors.nombre ? 'error' : ''}
                  />
                  {errors.nombre && <span className="error-message">{errors.nombre}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="apellido">Apellido *</label>
                  <input
                    type="text"
                    id="apellido"
                    value={patientData.apellido}
                    onChange={(e) => handleInputChange('apellido', e.target.value)}
                    placeholder="Apellido del paciente"
                    className={errors.apellido ? 'error' : ''}
                  />
                  {errors.apellido && <span className="error-message">{errors.apellido}</span>}
                </div>
              </div>
              
              {!compact && <div className="form-row">
                <div className="form-group">
                  <label htmlFor="fecha_nacimiento">Fecha de Nacimiento *</label>
                  <input
                    type="date"
                    id="fecha_nacimiento"
                    value={patientData.fecha_nacimiento}
                    onChange={(e) => handleInputChange('fecha_nacimiento', e.target.value)}
                    className={errors.fecha_nacimiento ? 'error' : ''}
                    max={new Date().toISOString().split('T')[0]}
                  />
                  {errors.fecha_nacimiento && <span className="error-message">{errors.fecha_nacimiento}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="genero">Género <span className="label-optional">opcional</span></label>
                  <select
                    id="genero"
                    value={patientData.genero}
                    onChange={(e) => handleInputChange('genero', e.target.value)}
                  >
                    <option value="">Seleccionar género</option>
                    <option value="femenino">Femenino</option>
                    <option value="masculino">Masculino</option>
                    <option value="no-binario">No binario</option>
                    <option value="prefiero-no-decir">Prefiero no decir</option>
                  </select>
                </div>
              </div>}
            </div>
            
            {/* Contacto */}
            <div className="form-section">
              {!compact && <h3>Información de Contacto</h3>}
              
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={patientData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="paciente@ejemplo.com"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="telefono">Teléfono *</label>
                <input
                  type="tel"
                  id="telefono"
                  value={patientData.telefono}
                  onChange={(e) => handleInputChange('telefono', e.target.value)}
                  placeholder="+56 9 1234 5678"
                  className={errors.telefono ? 'error' : ''}
                />
                {errors.telefono && <span className="error-message">{errors.telefono}</span>}
              </div>
              
              {!compact && <div className="form-group">
                <label htmlFor="direccion">Dirección</label>
                <input
                  type="text"
                  id="direccion"
                  value={patientData.direccion}
                  onChange={(e) => handleInputChange('direccion', e.target.value)}
                  placeholder="Dirección completa"
                />
              </div>}
            </div>
            
            {!compact && <>
            {/* Antecedentes médicos */}
            <div className="form-section">
              <h3>Antecedentes Clínicos <span className="section-optional">opcional</span></h3>

              <div className="form-group">
                <label htmlFor="condiciones_medicas">
                  Antecedentes médicos <span className="label-optional">opcional</span>
                </label>
                <textarea
                  id="condiciones_medicas"
                  value={patientData.condiciones_medicas}
                  onChange={(e) => handleInputChange('condiciones_medicas', e.target.value)}
                  placeholder="Enfermedades crónicas, diagnósticos previos, condiciones relevantes..."
                  rows={3}
                  className="form-textarea"
                />
              </div>

              <div className="form-group">
                <label htmlFor="medicamentos_actuales">
                  Medicamentos actuales <span className="label-optional">opcional</span>
                </label>
                <textarea
                  id="medicamentos_actuales"
                  value={patientData.medicamentos_actuales}
                  onChange={(e) => handleInputChange('medicamentos_actuales', e.target.value)}
                  placeholder="Medicamentos que toma actualmente, dosis si las conoce..."
                  rows={2}
                  className="form-textarea"
                />
              </div>
            </div>

            {/* Contacto de emergencia */}
            <div className="form-section">
              <h3>Contacto de Emergencia <span className="section-optional">opcional</span></h3>

              <div className="form-group">
                <label htmlFor="contacto_emergencia_nombre">Nombre de Contacto</label>
                <input
                  type="text"
                  id="contacto_emergencia_nombre"
                  value={patientData.contacto_emergencia_nombre}
                  onChange={(e) => handleInputChange('contacto_emergencia_nombre', e.target.value)}
                  placeholder="Nombre del contacto de emergencia"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contacto_emergencia_telefono">Teléfono de Contacto</label>
                <input
                  type="tel"
                  id="contacto_emergencia_telefono"
                  value={patientData.contacto_emergencia_telefono}
                  onChange={(e) => handleInputChange('contacto_emergencia_telefono', e.target.value)}
                  placeholder="+56 9 1234 5678"
                />
              </div>
            </div>
            </>}
          </div>
          
          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="cancel-btn"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner"></div>
                  Registrando...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus"></i>
                  Registrar Paciente
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PatientRegistration;